<template>
    <div class="flex flex-col">
        <BlocksAnimation
            class="container mx-auto"
            :class="{
                'text-center': block.HeadingPosition === 'centred',
            }"
        >
            <small class="text-base leading-base font-ux uppercase font-semibold text-subheading">
                {{ block.Subheading }}
            </small>
            <h2 class="mb-4 font-semibold text-4xl leading-4xl font-brand-primary text-heading mt-3">
                {{ block.Heading }}
            </h2>

            <p class="font-ux text-neutral-subtle">
                {{ block.BodyText }}
            </p>
        </BlocksAnimation>

        <div v-if="items.length > 0" ref="socialWrapper" class="flex-1 py-8">
            <Slider
                data-images
                :options="options"
                :style="{
                    height: imageHeight && block?.SocialFeedLayout === 'grid' ? `${imageHeight * 2}px` : 'auto',
                }"
                @init="initSlider"
            >
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    class="swiper-slide py-2"
                    :animation-offset="index"
                >
                    <BlocksAnimation :animation-offset="index">
                        <component
                            :is="block.LinkPosts ? NuxtLink : 'div'"
                            ref="sliderImages"
                            class="group relative overflow-hidden surface-background flex flex-col transition-all duration-150"
                            :class="{
                                'border border-subtle hover:shadow': block.SocialFeedStyle === 'cards',
                                'min-h-[350px]': block.SocialFeedStyle !== 'imageOnlyCaptionOnHover',
                                'radius-card-base': block.SocialFeedStyle !== 'imageWithCaptionBelow' && block.SpaceBetweenImages,
                                'mt-4': shouldOffsetImage(index),
                            }"
                            :to="item.url"
                        >
                            <div v-if="block.SocialFeedStyle === 'imageOnlyCaptionOnHover'" class="absolute left-4 top-4 z-40 opacity-80" data-mode="base-invert">
                                <DIcon :icon="item.type" icon-set="fa6-brands" class="text-neutral-base" size="w-5 h-5" />
                            </div>

                            <!-- image -->
                            <div
                                class="relative overflow-hidden flex-1"
                                :class="{
                                    'radius-card-base': block.SocialFeedStyle !== 'cards' && block.SpaceBetweenImages,
                                    'aspect-square': block.SocialFeedStyle === 'imageOnlyCaptionOnHover',
                                }"
                            >
                                <img class="absolute inset-0 object-cover h-full w-full" :src="item.image" loading="lazy" alt="">
                            </div>


                            <div
                                :data-mode="`${block.SocialFeedStyle === 'imageOnlyCaptionOnHover' ? 'color-invert' : 'base'}`"
                                class="flex-shrink-0 flex flex-col justify-end py-4"
                                :class="{
                                    'px-4': block.SocialFeedStyle === 'cards',
                                    'surface-background': block.SocialFeedStyle !== 'imageOnlyCaptionOnHover',
                                    'absolute inset-0 text-center flex items-center justify-center px-4 opacity-0 hover:opacity-100 transition-opacity duration-300': block.SocialFeedStyle === 'imageOnlyCaptionOnHover',
                                }"
                            >

                                <div v-if="block.SocialFeedStyle === 'imageOnlyCaptionOnHover'" class="pointer-events-none absolute z-0 inset-0 bg-overlay-brand-80 duration-300" />

                                <DIcon v-if="block.SocialFeedStyle === 'imageOnlyCaptionOnHover'" :icon="item.type" icon-set="fa6-brands" class="text-neutral-base mb-4" size="w-5 h-5" />

                                <p class="relative z-10 text-neutral-base text-sm leading-sm line-clamp-3">
                                    {{ item.text }}
                                </p>

                                <div
                                    class="flex items-center mt-3 text-xs text-neutral-subtle space-x-4"
                                    :class="{
                                        'justify-between': block.SocialFeedStyle === 'cards',
                                        'hidden': block.SocialFeedStyle === 'imageOnlyCaptionOnHover',
                                    }"
                                >
                                    <div class="flex items-center space-x-1">
                                        <DIcon :icon="item.type" icon-set="fa6-brands" class="shape-neutral-subtle" size="w-5 h-5" />
                                        <DateFormat :date="item.timestamp" from-now />
                                    </div>

                                    <div v-if="showLikesAndComments" class="flex items-center space-x-2">
                                        <div class="flex items-center space-x-1">
                                            <DIcon icon="heart" icon-set="ph" class="shape-neutral-subtle" size="w-5 h-5" />
                                            <span>{{item.likes}}</span>
                                        </div>

                                        <div class="flex items-center space-x-1">
                                            <DIcon icon="chat" icon-set="ph" class="shape-neutral-subtle" size="w-5 h-5" />
                                            <span>{{item.comments}}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </component>
                    </BlocksAnimation>
                </div>

                <template #navigation>
                    <BlocksAnimation class="flex items-center justify-between mt-9" :animation-offset="5">
                        <div class="flex items-center space-x-5">
                            <NuxtLink
                                v-for="(social, socialIndex) in socialLinks"
                                :key="socialIndex"
                                class="flex items-center space-x-1 shape-brand-base hover:shape-brand-strong"
                                :to="social.url"
                            >
                                <DIcon :icon="social.icon?.name" :icon-set="social.icon?.set" :type="social.icon?.type" size="w-5 h-5" />
                                <span class="text-sm font-medium leading-sm">{{ social.title }}</span>
                            </NuxtLink>
                        </div>

                        <div v-if="block.Carousel" class="flex items-center space-x-2">
                            <div class="button-prev">
                                <DButton theme="secondary" icon="caret-left" icon-set="ph" size="sm" />
                            </div>
                            <div class="button-next">
                                <DButton theme="secondary" icon="caret-right" icon-set="ph" size="sm" />
                            </div>
                        </div>
                    </BlocksAnimation>
                </template>
            </Slider>
        </div>
    </div>
</template>

<script setup>
    import { useResizeObserver } from '@vueuse/core'
    import { NuxtLink } from '#components'
    import { DIcon, DButton } from '@digistorm/spark'

    const props = defineProps({
        block: Object,
    })

    const { apiUrl, globalSets } = storeToRefs(useSiteStore())


    const items = ref([])
    const sliderImages = ref([])
    const socialWrapper = ref(null)
    const imageHeight = ref(0)

    const showLikesAndComments = computed(() => {
        return props.block?.ShowCommentsLikes
    })

    const gridSettings = computed(() => {
        if (props.block?.SocialFeedLayout !== 'grid') {
            return {}
        }

        return {
            grid: {
                fill: 'column',
                rows: 2
            }
        }
    })

    const shouldOffsetImage = (index) => {
        if (props.block?.SocialFeedLayout !== 'offset' || props.block?.SocialFeedLayout === 'grid') {
            return false
        }

        return index % 2 === 1
    }

    const options = ref({
        enabled: props.block?.Carousel,
        slidesPerView: 1,
        spaceBetween: props.block?.SpaceBetweenImages ? 24 : 0,
        loop: false,
        breakpoints: {
            500: {
                slidesPerView: 2,
                ...gridSettings.value,
            },
            768: {
                slidesPerView: 3,
                ...gridSettings.value,
            },
            1100: {
                slidesPerView: 4,
                ...gridSettings.value,
            },
        },
        ...gridSettings.value,
    })

    const integration = computed(() => {
        return get(props.block, 'Integration.integration')
    })

    const socialFeeds = computed(() => {
        return JSON.parse(get(props.block, 'Integration.socialFeeds'))
    })

    const footerSocials = computed(() => {
        return find(globalSets.value, { name: 'Socials' })?.socials_SocialLinks
    })

    const socialLinks = computed(() => {
        return map(socialFeeds.value, (social) => {
            const relatedSocial = find(footerSocials.value, (footerSocial) => {
                return toLower(footerSocial.title) === toLower(social)
            })
            return {
                url: relatedSocial?.url,
                title: relatedSocial?.title,
                icon: relatedSocial?.icon,
            }
        })
    })

    const setSlideHeight = (sliderEl) => {
        const spaceBetween = props.block?.SpaceBetweenImages ? 24 : 0
        imageHeight.value = sliderEl.getBoundingClientRect().height + spaceBetween
    }

    const initSlider = () => {
        nextTick(() => {
            const image = first(sliderImages.value)
            let sliderEl = null

            if (props.block?.LinkPosts) {
                sliderEl = image.$el
            } else {
                sliderEl = image
            }

            setSlideHeight(sliderEl)
        })
    }

    useResizeObserver(socialWrapper, (entries) => {
        const entry = entries[0]
        const image = entry.target.querySelector('.swiper-slide a')

        setSlideHeight(image)
    })


    onMounted(() => {
        $fetch(`${apiUrl.value}/integrations/social-feed`, {
            method: 'POST',
            body: {
                integration_id: integration.value,
                social_feeds: socialFeeds.value,
            }
        })
            .then(({ data }) => {
                items.value = get(data, 'posts') ?? []
            })
            .catch((error) => console.log('Error:', error))
    })

</script>

<style lang="scss">
    [data-images] {
        .swiper {
            @apply h-full;
        }
    }
</style>
